import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { AlertTriangle } from "lucide-react"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"

export default function TermsOfService() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>Terms of Service | Fusegu Security</title>
        <meta 
          name="description" 
          content="Terms of service and user agreement for Fusegu security scanning platform. Read our terms and conditions for using our services." 
        />
        <meta property="og:title" content="Terms of Service | Fusegu" />
        <meta property="og:description" content="Read the terms and conditions for using Fusegu's security scanning services." />
        <meta property="og:url" content="https://fusegusecurity.com/terms" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
        <div className="flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <FuseguLogo className="h-6 w-6 flex-shrink-0" />
            <span className="text-lg font-bold">Fusegu</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-purple-50 dark:from-slate-950 dark:to-purple-950">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Terms of Service
              </span>
            </h1>
            <p className="text-muted-foreground text-lg">Last updated: October 2025</p>
          </div>

          <div className="space-y-6">
            {/* Introduction */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Agreement to Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  These Terms of Service ("Terms") govern your access to and use of Fusegu Security's website scanning and security assessment platform (the "Service"). By creating an account or using our Service, you agree to be bound by these Terms.
                </p>
                <p>
                  If you do not agree to these Terms, you may not use the Service.
                </p>
              </CardContent>
            </Card>

            {/* Service Description */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Service Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  Fusegu Security provides automated security scanning and vulnerability assessment tools for websites and web applications. Our Service includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Comprehensive security scans using industry-standard tools</li>
                  <li>Vulnerability detection and reporting</li>
                  <li>SOC2 compliance mapping and technical assessments</li>
                  <li>White-label report generation with custom branding</li>
                  <li>Automated recurring security monitoring</li>
                </ul>
              </CardContent>
            </Card>

            {/* Account Registration */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Account Registration and Security</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p><strong>Eligibility:</strong> You must be at least 18 years old to use our Service.</p>

                <p><strong>Account Credentials:</strong></p>
                <ul className="list-disc pl-6 space-y-1 text-sm mb-4">
                  <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                  <li>You are responsible for all activities that occur under your account</li>
                  <li>You must notify us immediately of any unauthorized access or security breach</li>
                  <li>You may not share your account with others</li>
                </ul>

                <p><strong>Accurate Information:</strong></p>
                <p className="text-sm">You agree to provide accurate, current, and complete information during registration and to update it as necessary.</p>
              </CardContent>
            </Card>

            {/* Subscription and Billing */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Subscription Plans and Billing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Subscription Tiers</h3>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li><strong>Trial:</strong> 7-day free trial with 3 scans per day</li>
                    <li><strong>Pro:</strong> $49/month with 50 scans per day and full feature access</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Billing Terms</h3>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li>Subscriptions are billed monthly in advance</li>
                    <li>Payment is processed through Stripe, subject to their terms</li>
                    <li>Prices are subject to change with 30 days' notice</li>
                    <li>Failed payments may result in service suspension</li>
                    <li>Refunds are not provided for partial months or unused scans</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Cancellation</h3>
                  <p className="text-sm">
                    You may cancel your subscription at any time through the billing portal. Cancellation takes effect at the end of the current billing period. You will retain access until that date.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Acceptable Use */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Acceptable Use Policy
                </CardTitle>
                <CardDescription>You agree NOT to use our Service to:</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Scan websites you do not own or have explicit permission to scan</li>
                  <li>Conduct unauthorized penetration testing or hacking attempts</li>
                  <li>Violate any laws, regulations, or third-party rights</li>
                  <li>Distribute malware, viruses, or malicious code</li>
                  <li>Attempt to bypass rate limits or security measures</li>
                  <li>Reverse engineer, decompile, or disassemble our Service</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                  <li>Use automated scripts to create accounts or abuse the Service</li>
                  <li>Resell or redistribute our Service without authorization</li>
                  <li>Scan government, military, or critical infrastructure sites without proper authorization</li>
                </ul>
              </CardContent>
            </Card>

            {/* User Responsibilities */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Your Responsibilities</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li><strong>Authorization:</strong> You must have legal authority to scan any website you submit</li>
                  <li><strong>Compliance:</strong> You are responsible for complying with all applicable laws</li>
                  <li><strong>Data Accuracy:</strong> Results are based on automated scans and may contain false positives or miss certain vulnerabilities</li>
                  <li><strong>Independent Verification:</strong> You should independently verify scan results before taking action</li>
                  <li><strong>Professional Advice:</strong> Our Service does not constitute professional security consulting; consult experts for critical systems</li>
                </ul>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Intellectual Property</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Our Proprietary Rights</h3>
                  <p className="text-sm">
                    The Service, including all software, algorithms, text, graphics, and content, is owned by Fusegu Security and protected by copyright, trademark, and other intellectual property laws.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Your Content</h3>
                  <ul className="list-disc pl-6 space-y-1 text-sm">
                    <li>You retain ownership of URLs, branding assets, and data you provide</li>
                    <li>You grant us a license to use your data solely to provide the Service</li>
                    <li>We may use anonymized, aggregated data to improve our Service</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimers */}
            <Card className="border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-6 w-6" />
                  Important Disclaimers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">AS-IS SERVICE</h3>
                  <p className="text-sm uppercase">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">No Guarantee of Accuracy</h3>
                  <p className="text-sm">
                    While we strive for accuracy, automated security scans may:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-sm mt-2">
                    <li>Miss certain vulnerabilities (false negatives)</li>
                    <li>Report issues that are not actual vulnerabilities (false positives)</li>
                    <li>Not detect zero-day or novel attack vectors</li>
                    <li>Vary in results based on scan timing and website state</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Not a Substitute for Professional Services</h3>
                  <p className="text-sm">
                    Our Service does not replace professional security audits, penetration testing, or compliance assessments. For production systems or regulated industries, consult qualified security professionals.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card className="border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950 shadow-lg">
              <CardHeader>
                <CardTitle className="text-red-600 dark:text-red-400">Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm uppercase font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, FUSEGU SECURITY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL.
                </p>
                <p className="text-sm">
                  Our total liability for any claims arising from your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
                </p>
                <p className="text-sm">
                  This includes but is not limited to damages arising from:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Undetected security vulnerabilities</li>
                  <li>False positive or false negative scan results</li>
                  <li>Service downtime or unavailability</li>
                  <li>Data breaches or security incidents on scanned websites</li>
                  <li>Actions taken based on scan results</li>
                </ul>
              </CardContent>
            </Card>

            {/* Indemnification */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Indemnification</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  You agree to indemnify and hold harmless Fusegu Security from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm mt-2">
                  <li>Your use or misuse of the Service</li>
                  <li>Scanning websites without proper authorization</li>
                  <li>Violation of these Terms or applicable laws</li>
                  <li>Infringement of third-party rights</li>
                </ul>
              </CardContent>
            </Card>

            {/* Termination */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Termination</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p><strong>By You:</strong> You may terminate your account at any time through account settings or by contacting support.</p>

                <p><strong>By Us:</strong> We may suspend or terminate your account if you:</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Violate these Terms</li>
                  <li>Engage in fraudulent or abusive behavior</li>
                  <li>Fail to pay subscription fees</li>
                  <li>Use the Service in a manner that risks harm to others or our systems</li>
                </ul>

                <p className="text-sm"><strong>Effect of Termination:</strong> Upon termination, your access will cease, and we may delete your data after 30 days. You remain responsible for any outstanding fees.</p>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Changes to Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  We may modify these Terms at any time. Material changes will be notified via email or through the Service. Continued use after changes constitutes acceptance. If you disagree with changes, you must stop using the Service and cancel your account.
                </p>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Governing Law and Disputes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm">
                  These Terms are governed by the laws of the United States, without regard to conflict of law provisions.
                </p>
                <p className="text-sm">
                  Any disputes arising from these Terms or the Service shall be resolved through binding arbitration, except you may bring claims in small claims court.
                </p>
              </CardContent>
            </Card>

            {/* Miscellaneous */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">Miscellaneous</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-sm mb-1">Severability</h3>
                  <p className="text-sm">If any provision is found invalid, the remaining provisions continue in effect.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm mb-1">Entire Agreement</h3>
                  <p className="text-sm">These Terms constitute the entire agreement between you and Fusegu Security.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm mb-1">No Waiver</h3>
                  <p className="text-sm">Our failure to enforce any right or provision does not constitute a waiver.</p>
                </div>

                <div>
                  <h3 className="font-semibold text-sm mb-1">Assignment</h3>
                  <p className="text-sm">You may not assign these Terms without our consent. We may assign our rights at any time.</p>
                </div>
              </CardContent>
            </Card>

            {/* Contact Us */}
            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Contact Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  If you have any questions or concerns regarding these Terms of Service, please contact us at:
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="font-semibold">Fusegu Security</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Email: <a href="mailto:support@fusegusecurity.com" className="text-primary hover:underline">support@fusegusecurity.com</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer Navigation */}
          <div className="mt-12 pt-8 border-t text-center space-y-4">
            <div className="flex justify-center gap-6 text-sm">
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              <Link to="/" className="text-primary hover:underline">
                Home
              </Link>
              <Link to="/login" className="text-primary hover:underline">
                Sign In
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">
              © 2025 Fusegu Security. All rights reserved.
            </p>
          </div>
        </div>
      </main>
      </div>
    </>
  )
}
